import { FileAddOutlined, SendOutlined } from '@ant-design/icons';
import {
  addDealRoomComment,
  getDealRoomChatChannelByUser,
  getDealRoomComments,
  getDealRoomExternalComments,
  getDealRoomPeopleChannel,
  getDealRoomShortUrl,
} from '@app/api/deal-room.api';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { notificationController } from '@app/controllers/notificationController';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { IChat } from '@app/interfaces/chat';
import { IDealRoomChatChannel, IDealRoomShare } from '@app/interfaces/deal-room';
import { IChatPeople, IMessage } from '@app/interfaces/message';
import { getPeopleFullName, getTabId, getUserFullName, mapChatToMessage } from '@app/utils/utils';
import { Badge, Input, Tabs, Tooltip, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChatView } from '../ChatView/ChatView';
import * as S from './DealRoomChatSidebar.styles';
import { DealRoomUploadFileModal } from '@app/components/common/Modal/DealRoomUploadFileModal/DealRoomUploadFileModal';
import { useTranslation } from 'react-i18next';
import { IUser } from '@app/interfaces/user';
import { SOCKET_DEAL_ROOM_CHANNEL } from '@app/constants/variables';
import socket from '@app/utils/socket';
import { useResponsive } from '@app/hooks/useResponsive';
import { DealRoomWhatsAppModal } from '../DealRoomWhatsAppModal/DealRoomWhatsAppModal';
import { DealRoomEmailModal } from '../DealRoomEmailModal/DealRoomEmailModal';
import { useSearchParams } from 'react-router-dom';
import { DealRoomStatus } from '@app/constants/enums/deal-room';
import { DealRoomSmsModal } from '../DealRoomSmsModal/DealRoomSmsModal';
const { TabPane } = Tabs;

interface DealRoomChatSidebarProps {
  dealRoom: IDealRoomShare;
  isDrawer?: boolean;
  defaultChannel?: string | null;
  searchChannel?: string | null;
  open: boolean;
  chatMessage: number;
  className?: string;
  onClose: () => void;
  onFileSuccess?: () => void;
}

export const DealRoomChatSidebar: React.FC<DealRoomChatSidebarProps> = ({
  dealRoom,
  isDrawer = true,
  className,
  defaultChannel,
  searchChannel,
  open,
  chatMessage,
  onClose,
  onFileSuccess,
}) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.user);
  const [searchParams] = useSearchParams();
  const { isTablet } = useResponsive();

  const [isDealRoomClosed, setIsDealRoomClosed] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState(defaultChannel && defaultChannel === dealRoom?.groupChannelId ? '2' : '1');
  const [chats, setChats] = useState<IChat[]>([]);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [chatLoader, setChatLoader] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);
  const [waModal, setWaModal] = useState(false);
  const [smsModal, setSmsModal] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [chatUsers, setChatUsers] = useState<IChatPeople[]>([]);
  const [people, setPeople] = useState<IChatPeople | null>(null);
  const [channel, setChannel] = useState<string>('');
  const [channels, setChannels] = useState<IDealRoomChatChannel[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [shortLink, setShortLink] = useState<string | null>(null);

  const fetchShortLink = async () => {
    setChatLoader(true);
    try {
      if (shortLink) {
        setChatLoader(false);
        return;
      }
      const response = await getDealRoomShortUrl(dealRoom?.id as string);
      setShortLink(response.shortUrl);
    } catch (error) {
    } finally {
      setChatLoader(false);
    }
  };

  const onChange = (key: string) => {
    setActiveTab(key);
    fetchChats(key);
    getChannels();
  };

  const getDisabledMessage = useMemo(() => {
    if (isDealRoomClosed) {
      return t('dealRooms.landing.chatClosedMessage');
    }
    if (disabled) {
      return t('dealRooms.landing.chatNotResponsibleDisabledMessage');
    }
    return t('dealRooms.landing.typeAMessage');
  }, [isDealRoomClosed, disabled, t]);

  const getChannels = useCallback(async () => {
    if (user) {
      try {
        const data = await getDealRoomChatChannelByUser(dealRoom?.id as string);
        setChannels(data);
      } catch (error) {}
    }
  }, [user, dealRoom?.id]);

  const getChannelId = useCallback(
    (tab = null) => {
      const aTab = tab ? tab : activeTab;
      return aTab === '1' ? channel : dealRoom?.groupChannelId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channel, dealRoom?.groupChannelId],
  );

  const fetchChats = useCallback(
    async (tab = null) => {
      setChatLoader(true);
      try {
        const id = getChannelId(tab);
        if (id) {
          const list = user
            ? await getDealRoomComments(dealRoom?.id as string, id)
            : await getDealRoomExternalComments(dealRoom?.id as string, id, {
                sessionId: dealRoom?.sessionId || null,
                uniqueId: dealRoom?.uniqueId || null,
              });
          if (!isDrawer && searchParams.get('cId')) {
            window.history.replaceState({}, '', `${window.location.pathname}`);
          }
          const hasChannel = dealRoom?.channels?.find((ch) => ch.channel === id);
          if (hasChannel) setChats(list);
        }
      } finally {
        setChatLoader(false);
      }
    },
    [
      getChannelId,
      user,
      dealRoom?.id,
      dealRoom?.sessionId,
      dealRoom?.uniqueId,
      dealRoom?.channels,
      isDrawer,
      searchParams,
    ],
  );

  const hasUnreadMessage = (channelId: string) => {
    const channel = channels.find((channel) => channel.channel === channelId);
    return channel ? channel.unread : false;
  };

  const onWhatsAppClick = async () => {
    await fetchShortLink();
    setWaModal(true);
  };

  const onSmsClick = async () => {
    await fetchShortLink();
    setSmsModal(true);
  };

  const onEmailClick = async () => {
    await fetchShortLink();
    setEmailModal(true);
  };

  const onUploadSuccess = () => {
    setDocumentModal(false);
    fetchChats(activeTab);
    if (onFileSuccess) onFileSuccess();
  };

  const onChatRefresh = (isFile: boolean) => {
    fetchChats(activeTab);
    if (isFile && onFileSuccess) onFileSuccess();
  };

  const onAddComment = async (message: string) => {
    const id = getChannelId(activeTab);
    if (message.trim() !== '' && !disabled && id && dealRoom?.id) {
      setChatLoader(true);
      try {
        await addDealRoomComment(dealRoom?.id as string, id, {
          message,
          sessionId: dealRoom?.sessionId || null,
          uniqueId: dealRoom?.uniqueId || null,
          userId: user?.id || null,
          tabId: getTabId() || null,
        });
        await fetchChats(activeTab);
        setMessage('');
      } catch (error) {
        notificationController.error({ message: (error as Error).message });
      } finally {
        setChatLoader(false);
      }
    }
  };

  const updateChatUsers = useCallback(() => {
    const people: IChatPeople[] = [];
    (dealRoom?.peoples || []).forEach((person) => {
      const hasUnreadMessage = channels.find((channel) => channel.dealRoomPeople === person.id && channel.unread);
      people.push({
        id: person.id,
        name: getPeopleFullName(person),
        phone: person.phone,
        image: undefined,
        isUser: false,
        unread: !!hasUnreadMessage,
      });
    });
    if (activeTab === '2') {
      if (dealRoom?.responsible) {
        people.push({
          id: (dealRoom?.responsible as IUser).id,
          name: getUserFullName(dealRoom?.responsible as IUser),
          image: ((dealRoom?.responsible as IUser)?.image as unknown as string) || undefined,
          isUser: true,
          unread: false,
        });
      }
    }
    setChatUsers(people);
  }, [activeTab, dealRoom?.peoples, dealRoom?.responsible, channels]);

  const onChangePeople = async (user: IChatPeople) => {
    const isTabFirst = activeTab === '1';
    if (user.id === people?.id && isTabFirst) return;
    setActiveTab('1');
    setChatLoader(true);
    try {
      const channel = await getDealRoomPeopleChannel(user.id);
      setChannel(channel.id);
      setPeople(user);
      setChannels((prev) => prev.map((ch) => (ch.channel === channel.id ? { ...ch, unread: false } : ch)));
      if (!isTabFirst) fetchChats('1');
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setChatLoader(false);
    }
  };

  useEffect(() => {
    updateChatUsers();
  }, [updateChatUsers]);

  useEffect(() => {
    getChannels();
  }, [getChannels]);

  useEffect(() => {
    const messages = chats.map((chat) =>
      mapChatToMessage(chat, true, false, user?.id || dealRoom?.dealRoomPeople || ''),
    );
    setMessages(messages);
  }, [chats, dealRoom?.dealRoomPeople, user]);

  useEffect(() => {
    setIsDealRoomClosed(dealRoom?.status === DealRoomStatus.CLOSED);
  }, [dealRoom?.status]);

  useEffect(() => {
    if (defaultChannel) {
      const channel = dealRoom?.channels?.find((ch) => ch.channel === defaultChannel);
      if (channel) {
        if (channel.dealRoomPeople) {
          setChannel(defaultChannel);
          const person = dealRoom?.peoples?.find((p) => p.id === channel.dealRoomPeople);
          if (person) {
            setPeople({
              id: person.id as string,
              name: getPeopleFullName(person),
              image: undefined,
              isUser: false,
              unread: false,
            });
            setActiveTab('1');
            return;
          }
        } else if (!isDrawer) {
          setChannel(defaultChannel);
          setActiveTab('2');
          return;
        }
      }
    }
    setChannel(dealRoom?.channelId || '');
    if (dealRoom?.peoples && dealRoom?.peoples.length > 0) {
      const firstPerson = dealRoom.peoples[0];
      setPeople({
        id: firstPerson.id as string,
        name: getPeopleFullName(firstPerson),
        image: undefined,
        isUser: false,
        unread: false,
      });
    }
  }, [defaultChannel, dealRoom?.channelId, dealRoom.groupChannelId, dealRoom?.channels, dealRoom.peoples, isDrawer]);

  useEffect(() => {
    if (chatMessage > 0) {
      getChannels();
    }
  }, [chatMessage, getChannels]);

  useEffect(() => {
    if (channel) {
      setMessages([]);
      setChats([]);
      fetchChats();
    }
  }, [channel, fetchChats]);

  useEffect(() => {
    const channel = getChannelId();
    if (!channel) setDisabled(true);
    if (user && user.id !== (dealRoom?.responsible as IUser)?.id) setDisabled(true);
    else setDisabled(false);
  }, [dealRoom?.responsible, getChannelId, user]);

  useEffect(() => {
    if (dealRoom?.id) {
      const chatConnectionName = channel && people ? `${SOCKET_DEAL_ROOM_CHANNEL}${channel}` : null;
      if (chatConnectionName) {
        socket.on(chatConnectionName, (data: { tabId: string | null }) => {
          if ((data.tabId && data.tabId === getTabId()) || activeTab === '2') return;
          fetchChats(activeTab);
        });
      }

      const groupChatConnectionName = dealRoom?.groupChannelId
        ? `${SOCKET_DEAL_ROOM_CHANNEL}${dealRoom?.groupChannelId}`
        : null;
      if (groupChatConnectionName) {
        socket.on(groupChatConnectionName, (data: { tabId: string | null }) => {
          if ((data.tabId && data.tabId === getTabId()) || activeTab === '1') return;
          fetchChats(activeTab);
        });
      }

      // Clean up the listener
      return () => {
        if (chatConnectionName) socket.off(chatConnectionName);
        if (groupChatConnectionName) socket.off(groupChatConnectionName);
      };
    }
  }, [activeTab, channel, dealRoom.channelId, dealRoom?.groupChannelId, dealRoom?.id, fetchChats, people]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';

      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      };
    }
  }, [open]);

  useEffect(() => {
    if (!isDrawer && searchChannel) {
      const channelInfo = dealRoom?.channels?.find((ch) => ch.channel === searchChannel);
      if (channelInfo) {
        if (channelInfo?.dealRoomPeople) {
          const person = dealRoom?.peoples?.find((p) => p.id === channelInfo.dealRoomPeople);
          if (person) {
            setPeople({
              id: person.id as string,
              name: getPeopleFullName(person),
              image: undefined,
              isUser: false,
              unread: false,
            });
            setActiveTab('1');
            setChannel(searchChannel);
            onClose();
          }
        } else {
          setActiveTab('2');
          setChannel(searchChannel);
          onClose();
        }
      }
    }
  }, [dealRoom?.channels, dealRoom?.peoples, isDrawer, onClose, searchChannel]);

  const content = (
    <>
      {documentModal && getChannelId(activeTab) && (
        <DealRoomUploadFileModal
          open={documentModal}
          onCancel={() => setDocumentModal(false)}
          onSuccess={onUploadSuccess}
          dealRoomId={dealRoom?.id as string}
          sessionId={dealRoom?.sessionId as string}
          uniqueId={dealRoom?.uniqueId as string}
          isChat={true}
          channelId={getChannelId(activeTab) as string}
        />
      )}
      {waModal && (
        <DealRoomWhatsAppModal
          dealRoom={dealRoom}
          shortLink={shortLink}
          open={waModal}
          people={dealRoom?.peoples || []}
          selectedPeople={activeTab === '1' ? people?.id : null}
          onClose={() => setWaModal(false)}
        />
      )}
      {smsModal && (
        <DealRoomSmsModal
          dealRoom={dealRoom}
          shortLink={shortLink}
          open={smsModal}
          people={dealRoom?.peoples || []}
          selectedPeople={activeTab === '1' ? people?.id : null}
          onClose={() => setSmsModal(false)}
        />
      )}
      {emailModal && (
        <DealRoomEmailModal
          dealRoom={dealRoom}
          shortLink={shortLink}
          open={emailModal}
          people={dealRoom?.peoples || []}
          selectedPeople={activeTab === '1' ? people?.id : null}
          onClose={() => setEmailModal(false)}
        />
      )}
      <S.ChatWrapper className={[!isDrawer ? 'chat-layout' : '', className].filter(Boolean).join(' ')}>
        <S.TabsWrapper activeKey={activeTab} centered onChange={onChange}>
          {channel && people && (
            <TabPane
              tab={
                <Badge dot={hasUnreadMessage(channel)} offset={[-5, 0]} size="default">
                  <Typography>
                    {t('dealRooms.landing.chatWith', {
                      name: user ? people.name : getUserFullName(dealRoom?.responsible as IUser),
                    })}
                  </Typography>
                </Badge>
              }
              key="1"
            >
              <Spinner spinning={chatLoader}>
                <ChatView
                  isDealRoomClosed={isDealRoomClosed}
                  status={dealRoom?.status}
                  isDrawer={isDrawer}
                  messages={messages}
                  user={user}
                  dealRoom={dealRoom}
                  users={chatUsers}
                  showUsers={user ? true : false}
                  people={people}
                  isTablet={isTablet}
                  onChangePeople={onChangePeople}
                  onWhatsAppClick={onWhatsAppClick}
                  onSmsClick={onSmsClick}
                  onEmailClick={onEmailClick}
                  onChatRefresh={onChatRefresh}
                />
              </Spinner>
            </TabPane>
          )}
          {dealRoom?.groupChannelId && (
            <TabPane
              tab={
                <Badge dot={hasUnreadMessage(dealRoom?.groupChannelId)} offset={[-5, 0]} size="default">
                  <Typography>
                    {t('dealRooms.landing.chatWithCommitteeMembers', {
                      name: getUserFullName(dealRoom?.responsible as IUser),
                    })}
                  </Typography>
                </Badge>
              }
              key="2"
            >
              <Spinner spinning={chatLoader}>
                <ChatView
                  isDealRoomClosed={isDealRoomClosed}
                  status={dealRoom?.status}
                  isDrawer={isDrawer}
                  messages={messages}
                  user={user}
                  dealRoom={dealRoom}
                  users={chatUsers}
                  showUsers={true}
                  isTablet={isTablet}
                  onChangePeople={user ? onChangePeople : undefined}
                  onWhatsAppClick={onWhatsAppClick}
                  onSmsClick={onSmsClick}
                  onEmailClick={onEmailClick}
                  onChatRefresh={onChatRefresh}
                />
              </Spinner>
            </TabPane>
          )}
        </S.TabsWrapper>
        <Tooltip title={disabled || isDealRoomClosed ? getDisabledMessage : null} placement="top">
          <S.InputWrapper>
            <Input
              placeholder={getDisabledMessage}
              suffix={
                chatLoader ? (
                  <Spinner spinning={chatLoader} />
                ) : (
                  <>
                    <S.ButtonWrapper>
                      <S.ButtonIcon>
                        <SendOutlined
                          onClick={() => onAddComment(message)}
                          className={!message || disabled || isDealRoomClosed ? 'disabled' : ''}
                        />
                      </S.ButtonIcon>
                      <S.ButtonIcon>
                        <FileAddOutlined
                          onClick={() => {
                            if (disabled || isDealRoomClosed) return;
                            setDocumentModal(true);
                          }}
                          className={disabled || isDealRoomClosed ? 'disabled' : ''}
                        />
                      </S.ButtonIcon>
                    </S.ButtonWrapper>
                  </>
                )
              }
              disabled={chatLoader || disabled || isDealRoomClosed}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPressEnter={() => onAddComment(message)}
            />
          </S.InputWrapper>
        </Tooltip>
      </S.ChatWrapper>
    </>
  );

  return isDrawer ? (
    <S.ChatDrawer title="צ'אט" placement="left" onClose={() => onClose()} open={open}>
      {content}
    </S.ChatDrawer>
  ) : (
    <>{content}</>
  );
};
